import classNames from 'classnames';
import React, { FC, memo, useCallback, useLayoutEffect } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { Box, useGetBoxRentalPriceString, useGetBoxRentalPriceStringWithVat } from 'entities/Box';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { getGlobalSettings } from 'app/appState';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { useScrollToElement } from 'shared/utils/hooks/useScrollToElement';
import { UserAccountType } from 'entities/User';
import { FeaturesList } from 'shared/ui/FeaturesList';
import { WarehouseForBooking } from 'entities/Warehouse';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { InvoiceFrequencyType } from 'entities/Invoice';
import { SizeCodeType } from 'entities/SizeCode';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';

interface ModernBoxCardProps {
  box: Box;
  selectedBoxId: string | undefined;
  hasRentDiscounts: boolean;
  hasGeneralDiscounts: boolean;
  onSelectBox: (box: Box) => void;
  onBookBox: (box: Box) => void;
  accountType?: Nullable<UserAccountType>;
  warehouseInfo?: Nullable<WarehouseForBooking>;
}

export const ModernBoxCard: FC<ModernBoxCardProps> = memo((props) => {
  const { box, selectedBoxId, onSelectBox, onBookBox, warehouseInfo, accountType } = props;

  const { t } = useAppTranslation('booking');
  const { scrollToElement, scrollRef } = useScrollToElement('center', 500);

  const globalSettings = useAppSelector(getGlobalSettings);

  const showPriceWithVat = globalSettings?.showPriceWithVat;

  // const rentalPrice = useGetBoxRentalPriceString(box);

  const boxMonthRentalPriceWithVat = useGetBoxRentalPriceStringWithVat(box, InvoiceFrequencyType.MONTH, warehouseInfo, accountType);

  const boxMonthRentalPrice = useGetBoxRentalPriceString(box);

  const rentalPrice = showPriceWithVat ? boxMonthRentalPriceWithVat : boxMonthRentalPrice;

  const isBoxSelected = selectedBoxId === box.boxId;
  const boxImage = box.imageUrl || box.sizeCode?.imageUrl;

  const selectBox = useCallback((): void => {
    onSelectBox(box);
  }, [box, onSelectBox]);

  const bookBox = useCallback((): void => {
    onBookBox(box);
  }, [box, onBookBox]);

  useLayoutEffect(() => {
    if (isBoxSelected) {
      scrollToElement();
    }
  }, [isBoxSelected, scrollToElement]);

  return (
    <div
      ref={scrollRef}
      className={classNames('border rounded-lg p-4 cursor-pointer transition-all duration-200', {
        'border-2 border-accent bg-green-50': isBoxSelected,
        'border-2 border-gray-200 hover:border-gray-300 bg-white': !isBoxSelected,
      })}
      onClick={selectBox}
    >
      <div className="flex gap-4">
        {boxImage && <img src={boxImage} alt={box.name} className="w-24 h-24 object-cover rounded-md flex-shrink-0" />}
        <div className="flex-1">
          <div className="flex gap-2">
            <span className="font-semibold text-lg text-primary font-tektur">{t('Box')}</span>
            <span className="font-semibold text-lg text-primary uppercase font-tektur">{box.name}</span>
          </div>

          <div className="text-sm text-grey flex flex-col">
            {box.sizeCode.sizeCodeType !== SizeCodeType.CUBIC_BOX && (
              <span className="text-sm text-grey">{`${t('Square')} ${formatDecimal(roundNumber(box.width * box.length))} м²`}</span>
            )}
            {formatDecimal(box.width)} м × {formatDecimal(box.length)} м × {formatDecimal(roundNumber(box.height))} м
            <span className="text-sm text-grey">{`${t('Value')} ${formatDecimal(
              roundNumber(box.width * box.length * box.height),
            )} м³`}</span>
          </div>

          {box.description && (
            <div className="text-sm text-primaryLight" dangerouslySetInnerHTML={{ __html: getLocalizedString(box.description) }} />
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {box.floor && <FeaturesList features={t('{{floor}}', { floor: getLocalizedString(box.floor.name) })} theme="base" outlined />}
            </div>

            <div className="flex items-center gap-4">
              <div className="text-2xl font-bold text-primary font-tektur">{rentalPrice}</div>
              <Button onClick={bookBox} theme="primary" size="medium">
                {t('Book')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
